import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { SalesOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { HubHoverTable } from "@/components/hub/HubHoverTable";
import { apiFetch } from "@/lib/api";
import {
  commissionsFciVolumeQueryKey,
  commissionsStableListQueryOptions,
} from "@/pages/commissions/commissionsApi";

export type RepFciVolumeBreakdownRow = {
  sales_order_id: number;
  sales_order_number: string;
  sku: string;
  quantity: number;
};

/** Hover hints must not scroll internally; cap only when the breakdown is very large. */
const FCI_VOLUME_BREAKDOWN_ROW_CAP = 20;
const FCI_VOLUME_BREAKDOWN_TRUNCATE_THRESHOLD = 50;

function formatQuantity(raw: number): string {
  if (!Number.isFinite(raw)) {
    return "—";
  }
  return Math.round(raw).toLocaleString("en-US");
}

function RepFciVolumeBreakdownTable({ rows }: { rows: RepFciVolumeBreakdownRow[] }): JSX.Element {
  const shouldTruncate = rows.length >= FCI_VOLUME_BREAKDOWN_TRUNCATE_THRESHOLD;
  const visibleRows = shouldTruncate ? rows.slice(0, FCI_VOLUME_BREAKDOWN_ROW_CAP) : rows;
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <HubHoverTable
      className="max-w-[28rem]"
      tableClassName="w-full text-xs"
      columns={[
        {
          id: "salesOrder",
          header: "Sales order",
          overflow: "nowrap",
          cell: (row) => (
            <SalesOrderDetailLink salesOrderId={row.sales_order_id} salesOrderNumber={row.sales_order_number} />
          ),
        },
        {
          id: "sku",
          header: "FCI SKU",
          overflow: "nowrap",
          cellClassName: "font-mono text-[11px] text-slate-700",
          cell: (row) => row.sku,
        },
        {
          id: "qty",
          header: "Qty",
          align: "right",
          overflow: "nowrap",
          cellClassName: "text-slate-700",
          cell: (row) => formatQuantity(row.quantity),
        },
      ]}
      rows={visibleRows}
      rowKey={(row) => `${String(row.sales_order_id)}-${row.sku}`}
      footer={
        hiddenCount > 0 ? (
          <p className="mt-1.5 text-[11px] text-slate-500">
            and {hiddenCount.toLocaleString("en-US")} more line{hiddenCount === 1 ? "" : "s"}
          </p>
        ) : null
      }
    />
  );
}

export function RepFciVolumeHoverHint({
  repAgencyId,
  calendarYear,
}: {
  repAgencyId: number;
  calendarYear: number;
}): JSX.Element {
  const detailQuery = useQuery({
    queryKey: commissionsFciVolumeQueryKey(repAgencyId, calendarYear),
    queryFn: () =>
      apiFetch<{ rows: RepFciVolumeBreakdownRow[] }>(
        `/api/commissions/rates/${String(repAgencyId)}/fci-volume?year=${String(calendarYear)}`,
      ),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    placeholderData: commissionsStableListQueryOptions.placeholderData,
  });

  if (detailQuery.isPending && detailQuery.data == null) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading FCI volume…
      </p>
    );
  }

  if (detailQuery.isError) {
    return <p className="text-xs text-slate-600">Could not load FCI volume breakdown.</p>;
  }

  const rows = detailQuery.data?.rows ?? [];
  if (rows.length === 0) {
    return <p className="text-xs text-slate-600">No product-category sales order lines in {calendarYear}.</p>;
  }

  return <RepFciVolumeBreakdownTable rows={rows} />;
}
